/**
 * `useActiveTime` — #567 (Ishraf, Schoology spec §10): count the time a person
 * actually spends with the app in front of them, and hand it to `send` in small
 * beats.
 *
 * What counts: seconds while the tab is visible and the person has touched the
 * page (pointer, key, scroll, wheel, touch) in the last five minutes. A hidden tab
 * counts nothing and sends nothing; seconds banked before it was hidden wait for
 * the next visible beat (at most one beat is lost when a tab closes). Nothing but a
 * number of seconds leaves the page — no URL, no event.
 *
 * `accrue` is the pure step, exported for tests.
 */
import * as React from 'react';

/** How often banked time is advanced. */
export const ACTIVE_TICK_MS = 15_000;
/** How often banked time is sent. */
export const ACTIVE_SEND_MS = 60_000;
/** No input for this long and the time stops counting. */
export const ACTIVE_IDLE_MS = 5 * 60_000;
/** The most one beat sends; the server refuses more. */
export const ACTIVE_BEAT_MAX_SECONDS = 120;

/**
 * One tick: the seconds to bank for `elapsedMs` of wall time. Zero when the tab is
 * hidden or the person has been idle; never more than two ticks, so a sleeping
 * laptop's timer catching up cannot bank an hour at once.
 */
export function accrue(args: {
  elapsedMs: number;
  visible: boolean;
  sinceInputMs: number;
}): number {
  if (!args.visible || args.sinceInputMs > ACTIVE_IDLE_MS || args.elapsedMs <= 0) return 0;
  return Math.min(args.elapsedMs, ACTIVE_TICK_MS * 2) / 1000;
}

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'];

export function useActiveTime(send: (seconds: number) => void, opts: { enabled: boolean }): void {
  const sendRef = React.useRef(send);
  React.useEffect(() => {
    sendRef.current = send;
  }, [send]);

  React.useEffect(() => {
    if (!opts.enabled || typeof document === 'undefined') return undefined;
    const visible = (): boolean => document.visibilityState === 'visible';
    let lastInput = Date.now();
    let lastTick = Date.now();
    let banked = 0;

    const onInput = (): void => {
      lastInput = Date.now();
    };
    const onVisibility = (): void => {
      // Hidden time is never banked: restart the clock on either edge.
      lastTick = Date.now();
      if (visible()) lastInput = Date.now();
    };
    for (const e of INPUT_EVENTS) window.addEventListener(e, onInput, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    const tick = window.setInterval(() => {
      const now = Date.now();
      banked += accrue({
        elapsedMs: now - lastTick,
        visible: visible(),
        sinceInputMs: now - lastInput,
      });
      lastTick = now;
    }, ACTIVE_TICK_MS);

    const flush = window.setInterval(() => {
      if (!visible()) return;
      const seconds = Math.min(Math.floor(banked), ACTIVE_BEAT_MAX_SECONDS);
      if (seconds < 1) return;
      banked -= seconds;
      sendRef.current(seconds);
    }, ACTIVE_SEND_MS);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(flush);
      for (const e of INPUT_EVENTS) window.removeEventListener(e, onInput);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [opts.enabled]);
}
